import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Select, Spinner, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar, APP } from "../../components/mobileUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  useIsDesktop,
} from "../../components/desktopUi";
import {
  getMyBdcApi,
  getMyBdcRelativesApi,
  getBdcDisbursementListApi,
} from "../../api/finance.bdc.api";
import { formatVnd } from "../../utils/financeFormat";
import { EMP_BASE_PATH } from "../../config";

const BORDER = "#E1E1E1";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const SUBTLE = "#51607B";
const WARNING = "#F79D0D";
const LIMIT = 20;
const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "—");

const Row = ({ label, value, color }) => (
  <Flex justify="space-between" align="flex-start" gap="12px">
    <Text fontSize="13px" color={SUBTLE} flex="0.5">{label}</Text>
    <Text fontSize="14px" fontWeight={600} color={color || TEXT} flex="0.5" textAlign="right" noOfLines={2}>{value}</Text>
  </Flex>
);

const DisbursementCard = ({ item, name }) => {
  const isImport = item?.source === "IMPORT";
  const sourceText = isImport ? "Import" : "Giải ngân thủ công";
  const sourceColor = isImport ? APP.primary200 : WARNING;
  const money = (v) => (v != null ? formatVnd(v) : "—");
  return (
    <Box bg="white" border="1px solid" borderColor="#E5E9F0" borderRadius="12px" p="14px">
      <Flex align="center" justify="space-between" gap="8px">
        <Text fontSize="14px" fontWeight={600} color={TEXT} noOfLines={1} flex="1">{item?.apartmentCode || "—"}</Text>
        <Box bg="#F1F4F9" borderRadius="6px" px="8px" py="2px">
          <Text fontSize="13px" fontWeight={500} color={sourceColor}>{sourceText}</Text>
        </Box>
      </Flex>
      <Box h="1px" bg={BORDER} my="12px" />
      <Flex direction="column" gap="8px">
        {name ? <Row label="Tên" value={name} /> : null}
        <Row label="Ngày giải ngân" value={fmtD(item?.disbursementDate || item?.createdAt)} />
        <Row label="Tháng đối chiếu" value={item?.monthYear || "—"} />
        <Row label="Số trước thuế" value={money(item?.amountBeforeTax)} />
        <Row label="Thuế" value={money(item?.taxAmount)} />
        <Row label="Số tiền giải ngân" value={money(item?.disbursedAmount)} color={APP.primary200} />
      </Flex>
    </Box>
  );
};

const BdcDisbursement = () => {
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const [my, setMy] = useState(null);
  const [relatives, setRelatives] = useState([]);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(true);
  const [booting, setBooting] = useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");

  const reqId = useRef(0);
  const allIdsRef = useRef([]);
  const selRef = useRef("");

  const fetchList = useCallback(async (p, mode) => {
    const ids = allIdsRef.current;
    if (!ids.length) return;
    reqId.current += 1;
    const rid = reqId.current;
    setLoading(true);
    try {
      const sel = selRef.current;
      const params = sel
        ? { employeeId: sel, page: p, limit: LIMIT }
        : { employeeIds: ids, page: p, limit: LIMIT };
      const data = await getBdcDisbursementListApi(params);
      if (rid !== reqId.current) return;
      const list = data?.items || [];
      setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
      setHasNext(!!data?.pagination?.hasNextPage);
      setPage(p);
    } catch {
      if (rid === reqId.current && mode !== "append") setItems([]);
    } finally {
      if (rid === reqId.current) setLoading(false);
    }
  }, []);

  // Vào màn: nạp cá nhân + CTV rồi fetch danh sách MỘT LẦN (tránh giật/mất list)
  useEffect(() => {
    let alive = true;
    setBooting(true);
    setLoading(true);
    Promise.all([
      getMyBdcApi().catch(() => null),
      getMyBdcRelativesApi().catch(() => ({ items: [] })),
    ]).then(([myD, relD]) => {
      if (!alive) return;
      const rels = relD?.items || [];
      setMy(myD || {});
      setRelatives(rels);
      const ids = Array.from(
        new Set(
          [myD?.employeeId, ...rels.map((r) => r.employeeId)].filter(Boolean),
        ),
      );
      allIdsRef.current = ids;
      setBooting(false);
      if (ids.length) fetchList(1, "replace");
      else setLoading(false);
    });
    return () => {
      alive = false;
    };
  }, [fetchList]);

  const options = useMemo(() => {
    const opts = [{ value: "", label: "Tất cả" }];
    const seen = new Set();
    if (my?.employeeId) {
      seen.add(my.employeeId);
      opts.push({ value: my.employeeId, label: my.fullName || "Cá nhân" });
    }
    relatives.forEach((r) => {
      if (!r.employeeId || seen.has(r.employeeId)) return;
      seen.add(r.employeeId);
      opts.push({ value: r.employeeId, label: r.fullName || "—" });
    });
    return opts;
  }, [my, relatives]);

  const nameMap = useMemo(() => {
    const m = {};
    if (my?.employeeId) m[my.employeeId] = my.fullName || "";
    relatives.forEach((r) => {
      if (r.employeeId) m[r.employeeId] = r.fullName || "";
    });
    return m;
  }, [my, relatives]);

  const list = useMemo(() => {
    const seen = new Set();
    const out = [];
    items.forEach((it) => {
      const id = it?._id ? String(it._id) : "";
      if (id && seen.has(id)) return;
      if (id) seen.add(id);
      out.push(it);
    });
    return out;
  }, [items]);

  const onSelectEmployee = (val) => {
    const id = val || "";
    setSelectedEmployeeId(id);
    selRef.current = id;
    fetchList(1, "replace");
  };

  const pageBody = (
    <>
      <Box px="16px" pt="12px">
        <Flex align="center" justify="space-between" gap="12px" mb="12px">
          <Text fontSize="14px" fontWeight={600} color={TEXT} flexShrink={0}>Danh sách thanh toán</Text>
          <Select
            size="sm"
            maxW="55%"
            borderRadius="8px"
            value={selectedEmployeeId}
            onChange={(e) => onSelectEmployee(e.target.value)}
          >
            {options.map((o) => (
              <option key={o.value || "all"} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </Flex>

        {loading && !list.length ? (
          <Flex justify="center" py="40px"><Spinner color={APP.primary200} /></Flex>
        ) : list.length === 0 && !booting ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">Chưa có dữ liệu lịch sử thanh toán CCMG</Text>
        ) : (
          <Flex direction="column" gap="10px">
            {list.map((it, i) => (
              <DisbursementCard key={it._id || i} item={it} name={nameMap[it?.employeeId] || it?.employeeObject?.fullName || ""} />
            ))}
            {hasNext && (
              <Flex as="button" onClick={() => fetchList(page + 1, "append")} justify="center" py="12px" color={APP.primary200} fontWeight={600} fontSize="14px">
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>
    </>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage title="Lịch sử thanh toán CCMG" right={<DesktopBack onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/chung-chi-moi-gioi`)} />}>
        <DesktopPanel p="0" pb="24px">
          {pageBody}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" bg="#F6F8FC" minH="100dvh" pb="24px">
      <AppBar title="Lịch sử thanh toán CCMG" onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/chung-chi-moi-gioi`)} />
      {pageBody}
    </Box>
  );
};

export default BdcDisbursement;
